import { FC } from 'react';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import BookingsService from '@/shared/types/bookings/service.type';
import DialogName from '@/shared/types/dialogName.enum';

import BookingServiceAddContactsDialog from './components/BookingServiceAddContactsDialog';
import BookingServiceEditDialog from './components/BookingServiceEditDialog';
import BookingServiceRemoveContactsDialog from './components/BookingServiceRemoveContactsDialog';
import BookingServiceTable_v2 from './components/BookingServiceTable_v2/BookingServiceTable_v2';
import CreateBookingServiceDialog from './components/CreateBookingServiceDialog';
import Title from './components/Title';
import useBookingService from './hooks/useBookingService';

const BookingsServices: FC = () => {
  const { dialog, onUpdateDialogState } = useModalDialog<
    BookingsService,
    DialogName
  >();

  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const {
    refreshTableIndex,
    handleUpdateBookingService,
    handleAddContactsToBookingService,
    handleRemoveContactsFromBookingService,
    handleCreateBookingService,
  } = useBookingService({
    dialog,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
  });

  return (
    <div>
      <Title />

      <BookingServiceTable_v2
        refreshTableIndex={refreshTableIndex}
        setBookingServiceEditDialog={(open, bookingService) =>
          onUpdateDialogState(DialogName.Edit, {
            open,
            data: bookingService,
            errors: [],
          })
        }
        setBookingServiceAddContactsDialog={(open, bookingService) =>
          onUpdateDialogState(DialogName.AddContacts, {
            open,
            data: bookingService,
            errors: [],
          })
        }
        setBookingServiceRemoveContactsDialog={(open, bookingService) =>
          onUpdateDialogState(DialogName.RemoveContacts, {
            open,
            data: bookingService,
            errors: [],
          })
        }
        setCreateBookingServiceDialog={(open) => {
          onUpdateDialogState(DialogName.CreateBookingService, {
            open,
            errors: [],
          });
        }}
      />

      {dialog?.edit?.data && dialog.edit.open && (
        <BookingServiceEditDialog
          bookingService={dialog.edit.data}
          loading={dialog.edit.loading}
          errors={dialog.edit.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.Edit, { open: false });
          }}
          proceed={(formValues) => {
            void handleUpdateBookingService(formValues);
          }}
        />
      )}

      {dialog?.addContacts?.data && dialog?.addContacts?.open && (
        <BookingServiceAddContactsDialog
          errors={dialog?.addContacts?.errors}
          bookingService={dialog?.addContacts?.data}
          loading={dialog?.addContacts?.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.AddContacts, { open: false });
          }}
          proceed={(formValues) => {
            void handleAddContactsToBookingService(formValues);
          }}
        />
      )}

      {dialog?.removeContacts?.data && dialog?.removeContacts?.open && (
        <BookingServiceRemoveContactsDialog
          errors={dialog?.removeContacts?.errors}
          bookingService={dialog?.removeContacts?.data}
          loading={dialog?.removeContacts?.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.RemoveContacts, { open: false });
          }}
          proceed={(selectedAccountsIds) => {
            void handleRemoveContactsFromBookingService(selectedAccountsIds);
          }}
        />
      )}

      {dialog?.createBookingService?.open && (
        <CreateBookingServiceDialog
          errors={dialog?.createBookingService?.errors}
          loading={dialog?.createBookingService?.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.CreateBookingService, {
              open: false,
            });
          }}
          proceed={(values) => {
            void handleCreateBookingService(values);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default BookingsServices;
